import api, { baseUrl } from "@/services/api";
import { RecommendationType } from "@/services/recommendations/recommendations";
import { ResourceChannel, channelToPath } from "@/services/resources";
import { assertUnreachable } from "@/utils/typing";
import { computed, reactive } from "vue";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { RouteLocationRaw } from "vue-router";
import { toURLParam } from "@/utils/recommendations";
import { useAuthStore } from "@/store/auth";

export enum NotificationType {
  RESOURCE_ADDED = "RESOURCE_ADDED",
  RESOURCE_REMOVED = "RESOURCE_REMOVED",
  URGENT_RECOMMENDATION = "URGENT_RECOMMENDATION",
  AUTOPILOT_ENABLED = "AUTOPILOT_ENABLED",
  RESOURCE_UNAVAILABLE = "RESOURCE_UNAVAILABLE",
  TOKEN_EXPIRES_SOON = "TOKEN_EXPIRES_SOON",
  DLC_PAUSE_AD = "DLC_PAUSE_AD",
  DLC_ENABLE_AD = "DLC_ENABLE_AD",
  NEW_FEATURE_TOKEN = "NEW_FEATURE_TOKEN",
  FEED_ITEM_COMMENT = "FEED_ITEM_COMMENT",
  FEED_ITEM_COMMENT_REPLY = "FEED_ITEM_COMMENT_REPLY",
  FEED_ITEM_COMMENT_DELETED = "FEED_ITEM_COMMENT_DELETED",
  FEED_ITEM_REPLY_DELETED = "FEED_ITEM_REPLY_DELETED",
  BUDGET_OVERSPEND = "BUDGET_OVERSPEND",
  BUDGET_UNDERSPEND = "BUDGET_UNDERSPEND",
  BUDGET_PACING_OFF_TRACK = "BUDGET_PACING_OFF_TRACK",
  BUDGET_PERIOD_ENDING = "BUDGET_PERIOD_ENDING",
  BUDGET_REQUEST_SUBMITTED = "BUDGET_REQUEST_SUBMITTED",
  BUDGET_REQUEST_APPROVED = "BUDGET_REQUEST_APPROVED",
  BUDGET_REQUEST_REJECTED = "BUDGET_REQUEST_REJECTED",
  FEATURE_CONSENT_REQUESTED = "FEATURE_CONSENT_REQUESTED",
}
type ReadStatus = "READ" | "PARTIALLY_READ" | "UNREAD";

interface BaseNotification {
  pk: number;
  organization: number;
  createdAt: string;
  readStatus: ReadStatus;
}

// Technically this is not part of the first iteration spec..
export interface ResourceAddedNotification extends BaseNotification {
  type: NotificationType.RESOURCE_ADDED;
  details: {
    resourceChannel: ResourceChannel;
    user: number;
  };
}
export interface ResourceRemovedNotification extends BaseNotification {
  type: NotificationType.RESOURCE_REMOVED;
  details: {
    name: string;
    channel: ResourceChannel;
    user: number;
  };
}
export interface UrgentRecommendationNotification extends BaseNotification {
  type: NotificationType.URGENT_RECOMMENDATION;
  details: { recommendationType: RecommendationType };
}
export interface AutoPilotEnabledNotification extends BaseNotification {
  type: NotificationType.AUTOPILOT_ENABLED;
  details: { recommendationType: RecommendationType; user: number };
}
export interface ResourceUnavailableNotification extends BaseNotification {
  type: NotificationType.RESOURCE_UNAVAILABLE;
  details: { cause: string; id: number; channel: ResourceChannel };
}
export interface TokenExpiresSoonNotification extends BaseNotification {
  type: NotificationType.TOKEN_EXPIRES_SOON;
  details: {
    tokenId: number;
    expiresAt: string;
    isFirstNotification: boolean;
    channel: ResourceChannel;
  };
}

export interface DlcPauseAdNotification extends BaseNotification {
  type: NotificationType.DLC_PAUSE_AD;
  details: { autopilotEnabled: boolean };
}
export interface DlcEnableAdNotification extends BaseNotification {
  type: NotificationType.DLC_ENABLE_AD;
  details: { autopilotEnabled: boolean };
}

export interface NewFeatureTokenNotification extends BaseNotification {
  type: NotificationType.NEW_FEATURE_TOKEN;
  details: { channel: ResourceChannel; id: number };
}

export interface FeedItemCommentNotification extends BaseNotification {
  type: NotificationType.FEED_ITEM_COMMENT;
  details: {
    feedItemId: number;
    commentId: number;
    commentAuthorId: number;
  };
}

export interface FeedItemCommentReplyNotification extends BaseNotification {
  type: NotificationType.FEED_ITEM_COMMENT_REPLY;
  details: {
    commentId: number;
    replyId: number;
    replyAuthorId: number;
    feedItemId: number;
  };
}

export interface FeedItemCommentDeletedNotification extends BaseNotification {
  type: NotificationType.FEED_ITEM_COMMENT_DELETED;
  details: {
    commentId: number;
    feedItemId: number;
    deleterId: number;
  };
}

export interface FeedItemReplyDeletedNotification extends BaseNotification {
  type: NotificationType.FEED_ITEM_REPLY_DELETED;
  details: {
    replyId: number;
    commentId: number;
    feedItemId: number;
    deleterId: number;
  };
}

interface BudgetAlertNotificationDetails {
  budgetPlanId: number;
  budgetPlanName: string;
  budgetGroupId: number;
  budgetGroupName: string;
  budgetPeriodId: number;
  alertType: string;
  thresholdPercentage: string;
  actualValue: string;
  message: string;
}

export interface BudgetOverspendNotification extends BaseNotification {
  type: NotificationType.BUDGET_OVERSPEND;
  details: BudgetAlertNotificationDetails;
}

export interface BudgetUnderspendNotification extends BaseNotification {
  type: NotificationType.BUDGET_UNDERSPEND;
  details: BudgetAlertNotificationDetails;
}

export interface BudgetPacingOffTrackNotification extends BaseNotification {
  type: NotificationType.BUDGET_PACING_OFF_TRACK;
  details: BudgetAlertNotificationDetails;
}

export interface BudgetPeriodEndingNotification extends BaseNotification {
  type: NotificationType.BUDGET_PERIOD_ENDING;
  details: BudgetAlertNotificationDetails;
}

interface BudgetRequestNotificationDetails {
  budgetPlanId: number;
  budgetPlanName: string;
  // Null for group-target requests, which have no single period.
  budgetPeriodId: number | null;
  // Set for group-target requests; null for period-target requests.
  budgetGroupId: number | null;
  budgetRequestId: number;
  requestedAmount: string;
  currentAmount: string;
  adjustmentType: "REPLACE" | "INCREASE";
  reason: string;
}

export interface BudgetRequestSubmittedNotification extends BaseNotification {
  type: NotificationType.BUDGET_REQUEST_SUBMITTED;
  details: BudgetRequestNotificationDetails;
}

export interface BudgetRequestApprovedNotification extends BaseNotification {
  type: NotificationType.BUDGET_REQUEST_APPROVED;
  details: BudgetRequestNotificationDetails;
}

export interface BudgetRequestRejectedNotification extends BaseNotification {
  type: NotificationType.BUDGET_REQUEST_REJECTED;
  details: BudgetRequestNotificationDetails;
}

export interface FeatureConsentRequestedNotification extends BaseNotification {
  type: NotificationType.FEATURE_CONSENT_REQUESTED;
  details: {
    featureKey: string;
    featureTitle: string;
    requestedById: number;
    requestedByName: string;
  };
}

export type Notification =
  | ResourceAddedNotification
  | UrgentRecommendationNotification
  | AutoPilotEnabledNotification
  | ResourceRemovedNotification
  | ResourceUnavailableNotification
  | TokenExpiresSoonNotification
  | DlcEnableAdNotification
  | DlcPauseAdNotification
  | NewFeatureTokenNotification
  | FeedItemCommentNotification
  | FeedItemCommentReplyNotification
  | FeedItemCommentDeletedNotification
  | FeedItemReplyDeletedNotification
  | BudgetOverspendNotification
  | BudgetUnderspendNotification
  | BudgetPacingOffTrackNotification
  | BudgetPeriodEndingNotification
  | BudgetRequestSubmittedNotification
  | BudgetRequestApprovedNotification
  | BudgetRequestRejectedNotification
  | FeatureConsentRequestedNotification;

export function getNotificationCtaLink(
  notification: Notification,
): RouteLocationRaw | string {
  switch (notification.type) {
    case NotificationType.RESOURCE_ADDED:
      return { name: "resources" };
    case NotificationType.RESOURCE_REMOVED:
      return { name: "resources" };
    case NotificationType.RESOURCE_UNAVAILABLE:
      return { name: "resources" };
    case NotificationType.AUTOPILOT_ENABLED:
      return {
        name: "recommendation-details",
        params: {
          type: toURLParam(notification.details.recommendationType),
        },
      };
    case NotificationType.URGENT_RECOMMENDATION:
      return { name: "recommendations" };
    case NotificationType.TOKEN_EXPIRES_SOON:
      return { name: "resources" }; //TODO: Link to the auth flow for the channel
    case NotificationType.DLC_ENABLE_AD:
      return {
        name: "recommendation-details",
        params: {
          type: toURLParam(RecommendationType.DEAD_LINK_CHECKER),
        },
      };
    case NotificationType.DLC_PAUSE_AD:
      return {
        name: "recommendation-details",
        params: {
          type: toURLParam(RecommendationType.DEAD_LINK_CHECKER),
        },
      };
    case NotificationType.NEW_FEATURE_TOKEN:
      return `${baseUrl}/api/oauth/${
        channelToPath[notification.details.channel]
      }/authorize/?flow=refresh&token_id=${notification.details.id}`;
    case NotificationType.FEED_ITEM_COMMENT:
      return {
        name: "feed-post",
        params: { id: notification.details.feedItemId.toString() },
        query: {
          feedItemId: notification.details.feedItemId.toString(),
          commentId: notification.details.commentId.toString(),
        },
      };
    case NotificationType.FEED_ITEM_COMMENT_REPLY:
      return {
        name: "feed-post",
        params: { id: notification.details.feedItemId.toString() },
        query: {
          feedItemId: notification.details.feedItemId.toString(),
          commentId: notification.details.commentId.toString(),
          replyId: notification.details.replyId.toString(),
        },
      };
    case NotificationType.FEED_ITEM_COMMENT_DELETED:
      return {
        name: "feed-post",
        params: { id: notification.details.feedItemId.toString() },
        query: {
          feedItemId: notification.details.feedItemId.toString(),
        },
      };
    case NotificationType.FEED_ITEM_REPLY_DELETED:
      return {
        name: "feed-post",
        params: { id: notification.details.feedItemId.toString() },
        query: {
          feedItemId: notification.details.feedItemId.toString(),
          commentId: notification.details.commentId.toString(),
        },
      };
    case NotificationType.BUDGET_OVERSPEND:
    case NotificationType.BUDGET_UNDERSPEND:
    case NotificationType.BUDGET_PACING_OFF_TRACK:
    case NotificationType.BUDGET_PERIOD_ENDING:
    case NotificationType.BUDGET_REQUEST_SUBMITTED:
    case NotificationType.BUDGET_REQUEST_APPROVED:
    case NotificationType.BUDGET_REQUEST_REJECTED:
      return {
        name: "budget_tool_detail",
        params: { id: notification.details.budgetPlanId.toString() },
      };
    case NotificationType.FEATURE_CONSENT_REQUESTED:
      return { name: "consent_settings" };
    default:
      assertUnreachable(notification);
  }
}

export function listAllNotifications(): Promise<Notification[]> {
  return api.get("/notifications/").then((response) => {
    // A non-array 2xx body means a non-JSON response (e.g. HTML) slipped through.
    if (!Array.isArray(response.data)) {
      throw new Error("Notifications response was not an array");
    }
    return response.data;
  });
}

export function useNotificationsQuery() {
  const authStore = useAuthStore();
  return reactive(
    useQuery({
      queryKey: ["notifications"],
      queryFn: listAllNotifications,
      enabled: computed(() => authStore.isAuthed),
    }),
  );
}

export type UpdateNotificationPayload = Pick<Notification, "pk" | "readStatus">;

export function updateNotification(
  notification: UpdateNotificationPayload,
): Promise<Notification> {
  return api
    .put(`/notifications/${notification.pk}/`, notification)
    .then((res) => res.data);
}

export function useUpdateNotificationMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: updateNotification,
      onSuccess: (updatedNotification) => {
        queryClient.setQueryData<Notification[]>(
          ["notifications"],
          (notifications) =>
            notifications?.map((notification) =>
              notification.pk === updatedNotification.pk
                ? updatedNotification
                : notification,
            ),
        );
      },
    }),
  );
}

function readAllNotifications(): Promise<void> {
  return api.post("/notifications/mark_all_read/");
}

export function useReadAllNotificationsMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: readAllNotifications,
      onSettled: () =>
        queryClient.invalidateQueries({ queryKey: ["notifications"] }),
    }),
  );
}
